import { useState, type ReactNode } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Server, Box, Ban, Trash2, Settings2, Terminal, Play, Square, RotateCw, Pause, PlayCircle,
  FolderX, KeyRound, ArrowDownToLine, ArrowUpFromLine, ArrowLeft, Bot, AlertTriangle, RefreshCw,
  Cpu, Wrench, FolderGit2, Share2,
} from "lucide-react";
import { useAgent } from "@/api/queries/agents";
import { useUser } from "@/api/queries/users";
import {
  useRevokeAgent, useDeleteAgent,
  useStartAgent, useStopAgent, useRestartAgent, useSuspendAgent, useResumeAgent,
  useRotateGiteaIdentity,
} from "@/api/mutations/agents";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/SectionCard";
import { KvRow } from "@/components/PropGrid";
import { BackLink } from "@/components/DetailHeader";
import { StatusDot } from "@/components/StatusDot";
import { AgentPhaseBadge } from "@/lib/lifecycleBadge";
import { relativeTime } from "@/lib/relativeTime";
import { ApiError } from "@/api/client";
import { cn } from "@/lib/utils";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { SetAssignmentDialog } from "@/components/agent-actions/SetAssignmentDialog";
import { SetBrainSpecDialog } from "@/components/agent-actions/SetBrainSpecDialog";
import { FlashWorkspaceDialog } from "@/components/agent-actions/FlashWorkspaceDialog";
import { SessionsSection } from "@/components/agent-sessions/SessionsSection";
import { ConnectionsSection } from "@/components/agent-connections/ConnectionsSection";
import { ConnectionWakeSection } from "@/components/agent-connection-wake/ConnectionWakeSection";
import { RelaySection } from "@/components/agent-relay/RelaySection";
import { PulsSection } from "@/components/agent-puls/PulsSection";
import { DiscoverySection } from "@/components/agent-discovery/DiscoverySection";
import { ModelPolicySection } from "@/components/agent-model-policy/ModelPolicySection";
import { AnthropicAuthSection } from "@/components/agent-anthropic-auth/AnthropicAuthSection";
import { ProviderRoutingSection } from "@/components/agent-provider-routing/ProviderRoutingSection";
import { BudgetSection } from "@/components/agent-budget/BudgetSection";
import { WorkspaceSyncSection } from "@/components/agent-workspace-sync/WorkspaceSyncSection";
import { AgentConnectorsSection } from "@/components/agent-connectors/AgentConnectorsSection";
import { McpStatusSection } from "@/components/agent-mcp-status/McpStatusSection";
import { AgentPluginsSection } from "@/components/agent-plugins/AgentPluginsSection";
import { TabBar, type TabDef } from "@/components/ui/tabs";
import { useExecAgent } from "@/api/mutations/agentExec";

// Themen-Tabs der linken Spalte (Aktionen bleiben darüber immer sichtbar).
const DETAIL_TABS: TabDef[] = [
  { value: "modell", label: "Modell & Zugang", icon: <Cpu className="h-4 w-4" /> },
  { value: "tools", label: "Tools", icon: <Wrench className="h-4 w-4" /> },
  { value: "workspace", label: "Workspace & Discovery", icon: <FolderGit2 className="h-4 w-4" /> },
  { value: "netzwerk", label: "Netzwerk & Sessions", icon: <Share2 className="h-4 w-4" /> },
];

// #design-pass W4: Aktions-Cluster (Label links + Button-Reihe), Design-Stil.
function ActionGroup({ label, danger, children }: { label: string; danger?: boolean; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2.5 border-b border-border py-3 last:border-0">
      <span className={cn("w-[104px] shrink-0 text-[11px] font-bold uppercase tracking-[.06em]", danger ? "text-danger" : "text-text3")}>{label}</span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

const DESIRED_META: Record<string, { tone: "ok" | "warn" | "muted"; label: string }> = {
  running: { tone: "ok", label: "Running" },
  suspended: { tone: "warn", label: "Suspended" },
  stopped: { tone: "muted", label: "Stopped" },
};

export function AgentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useAgent(id);
  const ownerQuery = useUser(data?.ownerId ?? undefined);

  // Aktiver Themen-Tab via URL-Query (deeplinkbar, wie die Listen-Filter).
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = DETAIL_TABS.some((t) => t.value === searchParams.get("tab"))
    ? (searchParams.get("tab") as string)
    : "modell";
  const setActiveTab = (value: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", value);
        return next;
      },
      { replace: true },
    );

  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [brainOpen, setBrainOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [archiveBeforeDelete, setArchiveBeforeDelete] = useState(true);
  const [keepRepoOnDelete, setKeepRepoOnDelete] = useState(false);
  const [resetWsOpen, setResetWsOpen] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateError, setRotateError] = useState<string | null>(null);

  const revokeMutation = useRevokeAgent();
  const deleteMutation = useDeleteAgent();
  const startM = useStartAgent();
  const stopM = useStopAgent();
  const restartM = useRestartAgent();
  const suspendM = useSuspendAgent();
  const resumeM = useResumeAgent();
  const rotateM = useRotateGiteaIdentity();
  const execM = useExecAgent();

  const errorMessage =
    error instanceof ApiError && error.status === 404 ? `Agent "${id}" nicht gefunden` : (error as Error | undefined)?.message;

  function handleRevoke() {
    if (!data) return;
    setRevokeError(null);
    revokeMutation.mutate(data.id, {
      onSuccess: () => { toast.success(`Agent ${data.id} revoked`); setRevokeOpen(false); },
      onError: (err) => { const msg = (err as Error).message; setRevokeError(msg); toast.error(`Revoke fehlgeschlagen: ${msg}`); },
    });
  }
  function handleDelete() {
    if (!data) return;
    setDeleteError(null);
    deleteMutation.mutate({ id: data.id, archive: archiveBeforeDelete, keepRepo: keepRepoOnDelete }, {
      onSuccess: () => { toast.success(`Agent ${data.id} gelöscht`); setDeleteOpen(false); navigate("/agents", { replace: true }); },
      onError: (err) => { const msg = (err as Error).message; setDeleteError(msg); toast.error(`Delete fehlgeschlagen: ${msg}`); },
    });
  }
  function handleRotateGitea() {
    if (!data) return;
    setRotateError(null);
    rotateM.mutate(data.id, {
      onSuccess: () => { toast.success(`Gitea-Token von ${data.id} rotiert`); setRotateOpen(false); },
      onError: (err) => { const msg = (err as Error).message; setRotateError(msg); toast.error(`Token-Rotation fehlgeschlagen: ${msg}`); },
    });
  }
  function handleSync(direction: "pull" | "push") {
    if (!data) return;
    const cmd =
      direction === "pull"
        ? ["sh", "-c", "cd /workspace && git pull --no-rebase"]
        : ["sh", "-c", "cd /workspace && git add -A && (git diff --cached --quiet || git commit -m 'manual checkpoint (admin)') && git push"];
    execM.mutate(
      { agentId: data.id, cmd, runAs: "claude", timeoutMs: 60_000 },
      {
        onSuccess: (res) =>
          res.exitCode === 0
            ? toast.success(`Workspace-${direction === "pull" ? "Pull" : "Push"}: ${data.id} ok`)
            : toast.error(`${direction} exit ${res.exitCode}: ${(res.stderr || res.stdout || "").slice(0, 200)}`),
        onError: (err) => toast.error(`Sync fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  const alreadyRevoked = data?.revokedAt != null;
  function runLifecycle(label: string, m: ReturnType<typeof useStartAgent>) {
    if (!data) return;
    m.mutate(data.id, {
      onSuccess: () => toast.success(`${label}: ${data.id}`),
      onError: (err) => toast.error(`${label} fehlgeschlagen: ${(err as Error).message}`),
    });
  }

  const phase = data?.lifecyclePhase;
  const hasNode = data?.assignment != null;
  const lcBusy = startM.isPending || stopM.isPending || restartM.isPending || suspendM.isPending || resumeM.isPending;
  const canStart = !!hasNode && !alreadyRevoked && ["stopped", "crashed", "crashloopbackoff", "unknown"].includes(phase ?? "");
  const canStop = !alreadyRevoked && data?.desiredRunState !== "stopped" && phase !== "unassigned" && phase !== "suspended";
  const canRestart = !!hasNode && !alreadyRevoked && phase !== "unassigned" && phase !== "suspended";
  const canSuspend = !alreadyRevoked && phase === "running";
  const canResume = !!hasNode && !alreadyRevoked && phase === "suspended";
  const isProblem = ["crashed", "crashloopbackoff", "degraded"].includes(phase ?? "");

  // ----- Loading / Error -----
  if (isLoading && !data) {
    return (
      <SectionCard testId="detail-loading">
        <div className="flex items-center gap-3 py-8 text-sm text-text3">
          <RefreshCw className="h-4 w-4 animate-spin" /> Lädt Agent…
        </div>
      </SectionCard>
    );
  }
  if (isError || !data) {
    return (
      <div className="space-y-5">
        <BackLink to="/agents" label="Agents" />
        <SectionCard testId="detail-error">
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <AlertTriangle className="h-6 w-6 text-danger" />
            <div className="text-sm text-text2">{errorMessage ?? "Konnte nicht laden"}</div>
            <Button variant="outline" size="sm" onClick={() => refetch()}><RefreshCw className="h-4 w-4" />Retry</Button>
          </div>
        </SectionCard>
      </div>
    );
  }

  const ownerNode: ReactNode = data.ownerId == null
    ? <span className="text-text3">—</span>
    : ownerQuery.isError
      ? <Badge variant="orphaned">orphaned: {data.ownerId}</Badge>
      : ownerQuery.data
        ? <Link to={`/users/${data.ownerId}`} className="mono text-accent-text hover:underline">{ownerQuery.data.email}</Link>
        : <span className="mono text-xs text-text3">{data.ownerId}</span>;
  const dm = DESIRED_META[data.desiredRunState] ?? DESIRED_META.stopped;

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <Link to="/agents" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-text3 transition-colors hover:text-text">
        <ArrowLeft className="h-[15px] w-[15px]" /> Agents
      </Link>

      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="grid h-[52px] w-[52px] shrink-0 place-items-center rounded-[13px] border border-accent-border bg-gradient-to-br from-accent-soft to-surface2 text-accent">
          <Bot className="h-[26px] w-[26px]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="m-0 text-[22px] font-extrabold tracking-tight text-text">{data.name}</h1>
            {alreadyRevoked ? <Badge variant="revoked">Revoked</Badge> : <AgentPhaseBadge phase={data.lifecyclePhase} />}
          </div>
          {data.description && <p className="mt-1.5 text-[13.5px] text-text2">{data.description}</p>}
          <span className="mono mt-1 inline-block text-xs text-text3">{data.id}</span>
        </div>
        {/* Kontextuelle Lifecycle-Buttons */}
        <div className="flex shrink-0 gap-2">
          {canStart && <Button size="sm" onClick={() => runLifecycle("Start", startM)} disabled={lcBusy}><Play className="h-4 w-4" />Start</Button>}
          {canStop && <Button variant="outline" size="sm" onClick={() => runLifecycle("Stop", stopM)} disabled={lcBusy}><Square className="h-4 w-4" />Stop</Button>}
          {canResume && <Button size="sm" onClick={() => runLifecycle("Resume", resumeM)} disabled={lcBusy}><PlayCircle className="h-4 w-4" />Resume</Button>}
          {canRestart && <Button variant="outline" size="sm" onClick={() => runLifecycle("Restart", restartM)} disabled={lcBusy}><RotateCw className="h-4 w-4" />Neustart</Button>}
        </div>
      </div>

      {/* Problem-Banner */}
      {isProblem && (
        <div className={cn("flex items-center gap-3 rounded border px-4 py-3", phase === "degraded" ? "border-warn-border bg-warn-bg" : "border-danger-border bg-danger-bg")}>
          <AlertTriangle className={cn("h-5 w-5 shrink-0", phase === "degraded" ? "text-warn" : "text-danger")} />
          <div className="flex-1">
            <div className={cn("text-[13.5px] font-bold", phase === "degraded" ? "text-warn" : "text-danger")}>
              {phase === "crashloopbackoff" ? "Container startet wiederholt neu (CrashLoopBackOff)" : phase === "crashed" ? "Container abgestürzt" : "Agent degradiert"}
            </div>
            <div className="mt-0.5 text-[12.5px] text-text2">Letztes Lebenszeichen {relativeTime(data.lastSeen)}</div>
          </div>
          <Button variant="outline" size="sm" asChild><Link to={`/agents/${data.id}/exec`}><Terminal className="h-4 w-4" />Exec / Logs</Link></Button>
        </div>
      )}

      {/* Grid: Main + Sidebar */}
      <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-[1fr_340px]">
        <div className="flex flex-col gap-[18px]" data-testid="agent-actions">
          {/* Aktionen */}
          <SectionCard icon={<Settings2 className="h-[18px] w-[18px]" />} title="Aktionen" description="Konfiguration & Lebenszyklus">
            <div className="-my-3">
              <ActionGroup label="Konfiguration">
                <Button variant="outline" size="sm" onClick={() => setAssignmentOpen(true)} data-testid="action-assignment"><Server className="h-4 w-4" />Assignment</Button>
                <Button variant="outline" size="sm" onClick={() => setBrainOpen(true)} data-testid="action-brainspec"><Box className="h-4 w-4" />Brain-Image</Button>
                <Button variant="outline" size="sm" asChild data-testid="action-config"><Link to={`/agents/${data.id}/config`}><Settings2 className="h-4 w-4" />Config</Link></Button>
                <Button variant="outline" size="sm" asChild data-testid="action-exec"><Link to={`/agents/${data.id}/exec`}><Terminal className="h-4 w-4" />Exec-Konsole</Link></Button>
              </ActionGroup>
              <ActionGroup label="Lifecycle">
                <Button variant="outline" size="sm" onClick={() => runLifecycle("Start", startM)} disabled={!canStart || lcBusy} data-testid="action-start"><Play className="h-4 w-4" />Start</Button>
                <Button variant="outline" size="sm" onClick={() => runLifecycle("Stop", stopM)} disabled={!canStop || lcBusy} data-testid="action-stop"><Square className="h-4 w-4" />Stop</Button>
                <Button variant="outline" size="sm" onClick={() => runLifecycle("Restart", restartM)} disabled={!canRestart || lcBusy} data-testid="action-restart"><RotateCw className="h-4 w-4" />Restart</Button>
                <Button variant="outline" size="sm" onClick={() => runLifecycle("Suspend", suspendM)} disabled={!canSuspend || lcBusy} data-testid="action-suspend"><Pause className="h-4 w-4" />Suspend</Button>
                <Button variant="outline" size="sm" onClick={() => runLifecycle("Resume", resumeM)} disabled={!canResume || lcBusy} data-testid="action-resume"><PlayCircle className="h-4 w-4" />Resume</Button>
              </ActionGroup>
              {data.workspaceRepo && (
                <ActionGroup label="Workspace">
                  <Button variant="outline" size="sm" onClick={() => handleSync("pull")} disabled={phase !== "running" || execM.isPending} data-testid="action-sync-pull"><ArrowDownToLine className="h-4 w-4" />Pull</Button>
                  <Button variant="outline" size="sm" onClick={() => handleSync("push")} disabled={phase !== "running" || execM.isPending} data-testid="action-sync-push"><ArrowUpFromLine className="h-4 w-4" />Push</Button>
                  <Button variant="outline" size="sm" onClick={() => setRotateOpen(true)} data-testid="action-gitea-rotate"><KeyRound className="h-4 w-4" />Gitea-Token rotieren</Button>
                  <Button variant="outline" size="sm" onClick={() => setResetWsOpen(true)} data-testid="action-workspace-flash"><FolderX className="h-4 w-4" />Flashen</Button>
                </ActionGroup>
              )}
              <ActionGroup label="Gefährlich" danger>
                <Button variant="destructive" size="sm" onClick={() => setRevokeOpen(true)} disabled={alreadyRevoked} data-testid="action-revoke"><Ban className="h-4 w-4" />{alreadyRevoked ? "Revoked" : "Revoke"}</Button>
                <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)} data-testid="action-delete"><Trash2 className="h-4 w-4" />Löschen</Button>
              </ActionGroup>
            </div>
          </SectionCard>

          {/* Themen-Tabs — gruppieren die ehemals gestapelten Sections */}
          <TabBar tabs={DETAIL_TABS} value={activeTab} onChange={setActiveTab} />

          {activeTab === "modell" && (
            <>
              {/* #224 (UC-3): Modell-Governance-Policy (Admin-Modell + Owner-Override + Whitelist) */}
              <ModelPolicySection agent={data} />
              {/* #227: Anthropic-Zugang dieses Agents (per-Agent-Override über den Plattform-Default) */}
              <AnthropicAuthSection agentId={data.id} />
              {/* #243: LLM-Provider-Routing dieses Agents (anderer Provider via LiteLLM-Gateway) */}
              <ProviderRoutingSection agentId={data.id} />
              {/* #244: Outbound-Relay-Policy (final/verbose) — modell-agnostische Antwort-Garantie */}
              <RelaySection agent={data} />
              {/* #223: Budget (Monats-Limit + Verbrauch + Block-Status) */}
              <BudgetSection agent={data} />
            </>
          )}

          {activeTab === "tools" && (
            <>
              {/* #231: Remote-MCP-Connectors für diesen Agent freischalten */}
              <AgentConnectorsSection agent={data} />
              {/* #237 (UC-status): Live-MCP-Status (Ist) — managed vs. manuell, mit Filtern */}
              <McpStatusSection agent={data} />
              {/* #232: Marketplace-Plugins für diesen Agent freischalten (Scope managed|owner + Drift) */}
              <AgentPluginsSection agent={data} />
              {/* #335: der Puls — Freischalt-Schalter + stille Meta-Zeilen (nie Inhalt).
                  Bewusst im Tools-Tab: der Schalter entscheidet, ob der Agent das Werkzeug
                  `mesh__puls` überhaupt bekommt. */}
              <PulsSection agent={data} />
            </>
          )}

          {activeTab === "workspace" && (
            <>
              {/* #229: Workspace-Live-Sync (Device-ID + Relay + freigeschaltete User-Geräte) */}
              <WorkspaceSyncSection agent={data} />
              {/* #226: Discovery (CV-Profil + Flags + Scope) */}
              <DiscoverySection agent={data} />
            </>
          )}

          {activeTab === "netzwerk" && (
            <>
              {/* A2A-Connections + Sessions (echte Komponenten/Logik) */}
              <ConnectionsSection agent={data} />
              {/* #245 (Teil B): Connection-Wake opt-in (aktiver Wake bei accepted) */}
              <ConnectionWakeSection agent={data} />
              <SessionsSection agentId={data.id} />
            </>
          )}
        </div>

        {/* Sidebar: Eigenschaften */}
        <SectionCard title="Eigenschaften">
          <div className="-my-[11px]">
            <KvRow label="ID" value={<span className="mono">{data.id}</span>} />
            <KvRow label="Typ" value={<Badge variant={data.type === "user-agent" ? "enrolled" : "default"}>{data.type}</Badge>} />
            <KvRow label="Liveness" value={<span className="inline-flex items-center gap-2"><StatusDot tone={data.status === "busy" ? "info" : data.status === "available" ? "ok" : "muted"} />{data.status}</span>} />
            <KvRow label="Soll-Zustand" value={<span className="inline-flex items-center gap-2"><StatusDot tone={dm.tone} />{dm.label}</span>} />
            <KvRow label="Owner" value={ownerNode} />
            <KvRow label="Brain-Image" value={data.brainSpec?.imageId ? <Link to={`/brain-images/${data.brainSpec.imageId}`} className="mono text-xs text-accent-text hover:underline">{data.brainSpec.imageId.slice(0, 12)}…</Link> : <span className="text-text3">—</span>} />
            <KvRow label="Node" value={data.assignment?.nodeId ? <Link to={`/nodes/${data.assignment.nodeId}`} className="mono text-accent-text hover:underline">{data.assignment.nodeId}</Link> : <span className="text-text3">—</span>} />
            <KvRow label="Workspace-Repo" value={data.workspaceRepo ? <a href={data.workspaceRepo.cloneUrl.replace(/\.git$/, "")} target="_blank" rel="noreferrer" className="mono text-xs text-accent-text hover:underline" data-testid="workspace-repo-link">{data.workspaceRepo.repoFullName}</a> : <span className="text-text3">— (keine Gitea-Anbindung)</span>} />
            <KvRow label="Deployed Commit" value={data.workspaceRepo?.deployedCommit ? <span className="mono text-xs" title={data.workspaceRepo.deployedCommit}>{data.workspaceRepo.deployedCommit.slice(0, 10)}</span> : <span className="text-text3">—</span>} />
            <KvRow label="Working-Directory" value={data.cwd || <span className="text-text3">—</span>} />
            <KvRow label="Capabilities" value={data.capabilities ?? <span className="text-text3">—</span>} />
            <KvRow label="Revoked" value={data.revokedAt ? relativeTime(data.revokedAt) : <span className="text-text3">nein</span>} />
            <KvRow label="Erstellt" value={<span className="text-text2">{relativeTime(data.createdAt)}</span>} />
            <KvRow label="Zuletzt gesehen" value={<span className="text-text2">{relativeTime(data.lastSeen)}</span>} last />
          </div>
        </SectionCard>
      </div>

      {/* Dialoge (Logik 1:1) */}
      {assignmentOpen && <SetAssignmentDialog open onOpenChange={setAssignmentOpen} agent={data} />}
      {brainOpen && <SetBrainSpecDialog open onOpenChange={setBrainOpen} agent={data} />}
      <ConfirmDialog
        open={revokeOpen}
        onOpenChange={setRevokeOpen}
        title="Agent revoken"
        description="Setzt revokedAt auf jetzt. Token wird sofort ungültig. Reversibel? Nein — Token muss neu generiert werden (Recreate-Workflow)."
        confirmText="Revoke"
        confirmVariant="destructive"
        isPending={revokeMutation.isPending}
        errorMessage={revokeError ?? undefined}
        onConfirm={handleRevoke}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Agent endgültig löschen"
        description="Diese Aktion ist NICHT umkehrbar. Agent-Row, Sessions, Peer-Connections, Attachments (inkl. Bytes) und — falls zugewiesen — das claude-home-Volume auf dem Worker-Host werden entfernt."
        confirmText="Endgültig löschen"
        confirmVariant="destructive"
        isPending={deleteMutation.isPending}
        errorMessage={deleteError ?? undefined}
        requiredTypeText={data.id}
        onConfirm={handleDelete}
      >
        <label className="flex items-center gap-2 text-sm text-text2">
          <input type="checkbox" checked={archiveBeforeDelete} onChange={(e) => setArchiveBeforeDelete(e.target.checked)} data-testid="archive-checkbox" />
          Volume vorher als Archiv (tar.gz) auf dem Worker-Host sichern
        </label>
        {data.workspaceRepo && (
          <label className="flex items-center gap-2 text-sm text-text2">
            <input type="checkbox" checked={keepRepoOnDelete} onChange={(e) => setKeepRepoOnDelete(e.target.checked)} data-testid="keep-repo-checkbox" />
            Gitea-Workspace-Repo behalten (wegrotieren auf <span className="mono text-xs">{data.workspaceRepo.repoFullName.split("/")[1]}-deleted-…</span> statt löschen)
          </label>
        )}
      </ConfirmDialog>
      {resetWsOpen && <FlashWorkspaceDialog agent={data} open={resetWsOpen} onOpenChange={setResetWsOpen} />}
      <ConfirmDialog
        open={rotateOpen}
        onOpenChange={setRotateOpen}
        title="Gitea-Token rotieren"
        description="Erzeugt eine frische Gitea-Identität (neuer scoped Token) für dasselbe Repo — bei verlorenem/kompromittiertem Token. Repo-Inhalt bleibt. Läuft der Agent, wird er neu gestartet (neue .gitconfig)."
        confirmText="Token rotieren"
        confirmVariant="destructive"
        isPending={rotateM.isPending}
        errorMessage={rotateError ?? undefined}
        onConfirm={handleRotateGitea}
      />
    </div>
  );
}
